import React, { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import axios from "axios";
import { format, formatDistanceToNow } from "date-fns";
import { PropagateLoader } from "react-spinners";
import { getCookie } from "../../utils/cookieHandler";
import { config } from "../../utils/config";
import { resetpageHeader } from "../../redux/pageHeaderSlice";

import {
  DocumentTextIcon,
  ChartBarIcon,
  PlusCircleIcon,
  UserPlusIcon,
  CalendarIcon,
  CreditCardIcon,
  ArrowPathIcon,
  ChevronRightIcon,
  BuildingOffice2Icon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/24/outline";

import "./DashboardPage.css";

const ADMIN_ROLE = process.env.REACT_APP_ADMIN_ROLE;
const FINANCE_ROLE = process.env.REACT_APP_FINANCE_ROLE;

// Parse any date string from server so it accurately matches the user's device local time
const parseDeviceDate = (dateVal) => {
  if (!dateVal) return null;
  if (dateVal instanceof Date) return isNaN(dateVal.getTime()) ? null : dateVal;

  if (typeof dateVal === "string") {
    const trimmed = dateVal.trim();
    if (!trimmed) return null;

    // 1. Date-only format (e.g. "2026-09-24") - construct in local midnight so day never shifts
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      const [y, m, d] = trimmed.split("-").map(Number);
      return new Date(y, m - 1, d);
    }

    // 2. Datetime format (e.g. "2026-09-24 08:00:00" or "2026-09-24T08:00:00")
    // Directus / MySQL stores datetimes in UTC. If the server response lacks the "Z" offset,
    // append "Z" so JavaScript parses it as UTC and converts it to the user's local device time.
    let iso = trimmed;
    if (iso.includes(" ") && !iso.includes("T")) {
      iso = iso.replace(" ", "T");
    }
    // If it doesn't already have a timezone offset (Z, +HH:mm, -HH:mm), it's UTC
    if (!iso.endsWith("Z") && !/[+-]\d{2}(:?\d{2})?$/.test(iso)) {
      iso += "Z";
    }

    const d = new Date(iso);
    if (!isNaN(d.getTime())) return d;
  }

  const fallback = new Date(dateVal);
  return isNaN(fallback.getTime()) ? null : fallback;
};

// Format helper: returns "22 Sep 2026"
const formatDisplayDate = (dateVal) => {
  if (!dateVal) return "-";
  try {
    const d = parseDeviceDate(dateVal);
    if (!d) return String(dateVal);
    return format(d, "dd MMM yyyy");
  } catch {
    return String(dateVal);
  }
};

// Format helper: returns relative time matching user device time (e.g. "less than a minute ago", "5 minutes ago", "2 days ago")
const formatRelativeTime = (dateVal) => {
  if (!dateVal) return "";
  try {
    const d = parseDeviceDate(dateVal);
    if (!d) return "";
    return formatDistanceToNow(d, { addSuffix: true });
  } catch {
    return "";
  }
};

// Format currency amounts safely without hardcoding a branch currency
const formatAmount = (val, currency = "") => {
  const num = parseFloat(val);
  const curr = currency ? ` ${currency}` : "";
  if (isNaN(num)) return `0.00${curr}`;
  return `${num.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}${curr}`;
};

// Status badge styling helper
const getStatusBadge = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "picked up") {
    return "bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-700/10";
  }
  if (s === "returned") {
    return "bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-600/20";
  }
  if (s === "open") {
    return "bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-600/20";
  }
  if (s === "draft") {
    return "bg-gray-100 text-gray-700 ring-1 ring-inset ring-gray-600/20";
  }
  if (s === "closed") {
    return "bg-purple-50 text-purple-700 ring-1 ring-inset ring-purple-700/10";
  }
  if (s === "canceled") {
    return "bg-red-50 text-red-700 ring-1 ring-inset ring-red-600/10";
  }
  return "bg-gray-50 text-gray-600 ring-1 ring-inset ring-gray-500/10";
};

const DashboardPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Clear any lingering pageHeader state (e.g. sequence buttons or billing toggles from other pages)
  useEffect(() => {
    dispatch(resetpageHeader());
  }, [dispatch]);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);

  const [dashboardData, setDashboardData] = useState({
    latestBillingDates: [],
    latestCharges: [],
    pendingReservations: [],
    pendingCompanies: [],
  });

  const [lastRefreshed, setLastRefreshed] = useState(null);

  // Fetch dashboard overview data
  const loadDashboardData = useCallback(async (tokenOverride = null) => {
    try {
      const token = tokenOverride || getCookie("_AT");
      if (!token) return;

      const response = await axios.get(
        `${config.apiUrl}/api/dashboard/overview`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data?.success && response.data?.data) {
        setDashboardData(response.data.data);
        setLastRefreshed(new Date());
        setError(null);
      }
    } catch (err) {
      console.error("Error loading dashboard data:", err);
      setError("Failed to load dashboard data. Please try again.");
    }
  }, []);

  // Authenticate user & role check
  useEffect(() => {
    const checkRoleAndLoad = async () => {
      try {
        setLoading(true);
        const token = getCookie("_AT");
        const cookieRole = getCookie("_r");

        if (!token) {
          setIsAuthorized(false);
          setAuthChecked(true);
          setLoading(false);
          return;
        }

        // Quick check via cookie if available
        if (
          cookieRole &&
          cookieRole !== ADMIN_ROLE &&
          cookieRole !== FINANCE_ROLE
        ) {
          setIsAuthorized(false);
          setAuthChecked(true);
          setLoading(false);
          return;
        }

        // Verify with backend user profile
        const userResp = await axios.get(`${config.apiUrl}/api/users/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const roleId =
          userResp.data?.role?.id ||
          (typeof userResp.data?.role === "string"
            ? userResp.data?.role
            : null) ||
          cookieRole;
        if (roleId === ADMIN_ROLE || roleId === FINANCE_ROLE) {
          setIsAuthorized(true);
          await loadDashboardData(token);
        } else {
          setIsAuthorized(false);
        }
      } catch (err) {
        console.error("Dashboard auth check error:", err);
        setError("Failed to verify user credentials");
      } finally {
        setAuthChecked(true);
        setLoading(false);
      }
    };

    checkRoleAndLoad();
  }, [loadDashboardData]);

  // Handle manual refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    await loadDashboardData();
    setRefreshing(false);
  };

  // 1. Initial Loading Spinner
  if (loading && !authChecked) {
    return (
      <div className="flex h-96 justify-center items-center">
        <PropagateLoader color="#0167ff" />
      </div>
    );
  }

  // 2. Role Gating: White screen for non-Admin/Finance roles
  if (authChecked && !isAuthorized) {
    return <div className="min-h-screen bg-white" />;
  }

  // 3. Error state with Retry button
  if (error && !dashboardData.latestBillingDates.length && !dashboardData.pendingReservations.length) {
    return (
      <div className="flex h-96 justify-center items-center">
        <div className="text-center p-8 bg-white rounded-xl shadow-sm border border-gray-200 max-w-md">
          <ExclamationCircleIcon className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <p className="text-gray-900 font-semibold text-lg mb-1">Unable to Load Dashboard</p>
          <p className="text-gray-500 text-sm mb-6">{error}</p>
          <button
            onClick={() => {
              setLoading(true);
              loadDashboardData().finally(() => setLoading(false));
            }}
            className="px-5 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition"
          >
            Retry Loading
          </button>
        </div>
      </div>
    );
  }

  const {
    latestBillingDates = [],
    latestCharges = [],
    pendingReservations = [],
    pendingCompanies = [],
  } = dashboardData;

  // Defensive client-side sorting: ensure latest billing dates are strictly descending (newest first)
  const sortedBillingDates = [...latestBillingDates].sort((a, b) =>
    (b.date || "").localeCompare(a.date || "")
  );

  // Defensive client-side sorting: ensure latest charges are strictly descending (newest first)
  const sortedCharges = [...latestCharges].sort((a, b) => {
    const timeA = a.date_created ? new Date(a.date_created).getTime() : 0;
    const timeB = b.date_created ? new Date(b.date_created).getTime() : 0;
    if (timeB !== timeA) return timeB - timeA;
    return (b.id || 0) - (a.id || 0);
  });

  return (
    <div className="custom-dashboard max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
      <Helmet>
        <title>Dashboard | Glide</title>
        <meta
          name="description"
          content="Financial & Operational Dashboard for Glide"
        />
      </Helmet>

      {/* ─── 1. QUICK ACTIONS ────────────────────────────────────────────── */}
      <div>
        <div className="flex items-center justify-between gap-4 mb-3">
          <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Quick Actions
          </h2>
          <div className="flex items-center gap-3">
            {lastRefreshed && (
              <span className="text-xs text-gray-500 hidden sm:inline-block">
                Updated {formatRelativeTime(lastRefreshed)}
              </span>
            )}
            <button
              type="button"
              onClick={handleRefresh}
              disabled={refreshing}
              className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 transition shadow-sm disabled:opacity-50"
            >
              <ArrowPathIcon
                className={`w-4 h-4 text-gray-500 ${
                  refreshing ? "animate-spin text-blue-600" : ""
                }`}
              />
              <span>{refreshing ? "Refreshing..." : "Refresh"}</span>
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Action 1: Smart Bill */}
          <button
            type="button"
            onClick={() => navigate("/smart-bill")}
            className="flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md hover:border-blue-300 transition text-left group"
          >
            <div className="flex-shrink-0 w-11 h-11 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition">
              <DocumentTextIcon className="w-6 h-6" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-gray-900 group-hover:text-blue-600 transition">
                Smart Bill
              </p>
              <p className="text-xs text-gray-500 truncate">
                Batch charges & adjustments
              </p>
            </div>
            <ChevronRightIcon className="w-5 h-5 text-gray-400 group-hover:translate-x-0.5 group-hover:text-blue-600 transition" />
          </button>

          {/* Action 2: Browse Sales Report */}
          <button
            type="button"
            onClick={() => navigate("/reports")}
            className="flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md hover:border-emerald-300 transition text-left group"
          >
            <div className="flex-shrink-0 w-11 h-11 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center group-hover:bg-emerald-600 group-hover:text-white transition">
              <ChartBarIcon className="w-6 h-6" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-gray-900 group-hover:text-emerald-600 transition">
                Sales Report
              </p>
              <p className="text-xs text-gray-500 truncate">
                Revenue & sales analysis
              </p>
            </div>
            <ChevronRightIcon className="w-5 h-5 text-gray-400 group-hover:translate-x-0.5 group-hover:text-emerald-600 transition" />
          </button>

          {/* Action 3: Create Reservation */}
          <button
            type="button"
            onClick={() => navigate("/reservations")}
            className="flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md hover:border-violet-300 transition text-left group"
          >
            <div className="flex-shrink-0 w-11 h-11 rounded-lg bg-violet-50 text-violet-600 flex items-center justify-center group-hover:bg-violet-600 group-hover:text-white transition">
              <PlusCircleIcon className="w-6 h-6" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-gray-900 group-hover:text-violet-600 transition">
                New Reservation
              </p>
              <p className="text-xs text-gray-500 truncate">
                Manage & add bookings
              </p>
            </div>
            <ChevronRightIcon className="w-5 h-5 text-gray-400 group-hover:translate-x-0.5 group-hover:text-violet-600 transition" />
          </button>

          {/* Action 4: Create Driver / Customer */}
          <button
            type="button"
            onClick={() => navigate("/customers")}
            className="flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md hover:border-amber-300 transition text-left group"
          >
            <div className="flex-shrink-0 w-11 h-11 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center group-hover:bg-amber-600 group-hover:text-white transition">
              <UserPlusIcon className="w-6 h-6" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-gray-900 group-hover:text-amber-600 transition">
                New Driver
              </p>
              <p className="text-xs text-gray-500 truncate">
                Driver & customer profiles
              </p>
            </div>
            <ChevronRightIcon className="w-5 h-5 text-gray-400 group-hover:translate-x-0.5 group-hover:text-amber-600 transition" />
          </button>
        </div>
      </div>

      {/* ─── 2. MIDDLE TWO-COLUMN ROW ────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left: Latest 4 Billing Dates (5 cols) */}
        <div className="lg:col-span-5 bg-white border border-gray-200 rounded-xl shadow-sm p-5 flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between pb-3 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
                  <CalendarIcon className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-gray-900">
                    Latest Billing Dates
                  </h3>
                  <p className="text-xs text-gray-500">
                    4 most recent unique event dates
                  </p>
                </div>
              </div>
              <span className="px-2 py-0.5 text-xs font-medium bg-blue-50 text-blue-700 rounded-full">
                Distinct
              </span>
            </div>

            <div className="mt-4 space-y-3">
              {sortedBillingDates.length === 0 ? (
                <div className="text-center py-8 text-gray-400 text-sm">
                  No billing dates recorded yet.
                </div>
              ) : (
                sortedBillingDates.map((item, idx) => {
                  const dateObj = parseDeviceDate(item.raw || item.date);
                  const dayNum = !dateObj
                    ? "-"
                    : format(dateObj, "dd");
                  const monthName = !dateObj
                    ? ""
                    : format(dateObj, "MMM");
                  const yearNum = !dateObj
                    ? ""
                    : format(dateObj, "yyyy");

                  return (
                    <div
                      key={item.date || idx}
                      className="flex items-center justify-between p-3 rounded-lg bg-gray-50 border border-gray-100 hover:border-gray-200 transition"
                    >
                      <div className="flex items-center gap-3">
                        {/* Mini Calendar Square */}
                        <div className="w-11 h-11 bg-white border border-gray-200 rounded-lg flex flex-col items-center justify-center shadow-2xs">
                          <span className="text-[10px] uppercase font-bold text-blue-600 leading-tight">
                            {monthName}
                          </span>
                          <span className="text-base font-extrabold text-gray-900 leading-tight">
                            {dayNum}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-900">
                            {formatDisplayDate(item.raw || item.date)}
                          </p>
                          <p className="text-xs text-gray-500">
                            {yearNum} Billing Cycle
                          </p>
                        </div>
                      </div>

                      <span className="text-xs font-medium px-2.5 py-1 bg-white border border-gray-200 text-gray-600 rounded-md shadow-2xs">
                        {formatRelativeTime(item.raw || item.date) || "Recent"}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className="mt-4 pt-3 border-t border-gray-100 text-xs text-gray-400 flex items-center justify-between">
            <span>Deduplicated across all billing events</span>
            <button
              onClick={() => navigate("/reports")}
              className="text-blue-600 font-medium hover:underline"
            >
              View in Sales Report →
            </button>
          </div>
        </div>

        {/* Right: Latest 5 Charges Added (7 cols) */}
        <div className="lg:col-span-7 bg-white border border-gray-200 rounded-xl shadow-sm p-5">
          <div className="flex items-center justify-between pb-3 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-emerald-50 text-emerald-600 rounded-lg">
                <CreditCardIcon className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-gray-900">
                  Latest 5 Charges Added
                </h3>
                <p className="text-xs text-gray-500">
                  Recent extra charges, fines, and adjustments
                </p>
              </div>
            </div>
            <span className="px-2 py-0.5 text-xs font-medium bg-emerald-50 text-emerald-700 rounded-full">
              Latest 5
            </span>
          </div>

          <div className="mt-4">
            {sortedCharges.length === 0 ? (
              <div className="text-center py-8 text-gray-400 text-sm">
                No recent charges recorded.
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {sortedCharges.map((ch) => (
                  <div
                    key={ch.id}
                    className="py-3 flex items-center justify-between gap-3 hover:bg-gray-50/70 rounded-lg px-2 -mx-2 transition"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                          {ch.typeName}
                        </span>
                        {ch.branchCode && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-100 uppercase">
                            {ch.branchCode}
                          </span>
                        )}
                        {ch.plate && (
                          <button
                            type="button"
                            onClick={() =>
                              ch.reservationId &&
                              navigate(`/reservations/${ch.reservationId}`)
                            }
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition"
                            title="Go to reservation"
                          >
                            {ch.plate}
                          </button>
                        )}
                        <span className="text-xs text-gray-400">
                          {formatRelativeTime(ch.date_created)}
                        </span>
                      </div>
                      {ch.description && (
                        <p className="text-xs text-gray-500 mt-1 truncate">
                          {ch.description}
                        </p>
                      )}
                    </div>

                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-bold text-gray-900">
                        {formatAmount(ch.amount, ch.currency)}
                      </p>
                      {ch.reservationId && (
                        <button
                          type="button"
                          onClick={() =>
                            navigate(`/reservations/${ch.reservationId}`)
                          }
                          className="text-[11px] text-blue-600 hover:underline"
                        >
                          Res #{ch.reservationId} →
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ─── 3. PENDING RESERVATIONS ─────────────────────────────────────── */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <div className="p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-gray-200">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-base font-bold text-gray-900">
                Pending Reservations
              </h3>
              <span className="px-2 py-0.5 text-xs font-semibold bg-orange-50 text-orange-700 ring-1 ring-inset ring-orange-600/20 rounded-full">
                {pendingReservations.length} Unbilled
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              Reservations with unsequenced or pending billing events requiring attention.
            </p>
          </div>

          <button
            type="button"
            onClick={() => navigate("/reservations")}
            className="inline-flex items-center text-xs font-semibold text-blue-600 hover:text-blue-800 transition"
          >
            View All Reservations →
          </button>
        </div>

        <div className="dashboard-table-container">
          {pendingReservations.length === 0 ? (
            <div className="p-8 text-center">
              <CheckCircleIcon className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
              <p className="text-sm font-medium text-gray-900">
                All reservations are fully billed!
              </p>
              <p className="text-xs text-gray-500">
                No active reservations currently have pending billing events.
              </p>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Vehicle
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer / Company
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Dates
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Balance
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {pendingReservations.map((res) => (
                  <tr
                    key={res.id}
                    className="hover:bg-blue-50/40 transition cursor-pointer"
                    onClick={() => navigate(`/reservations/${res.id}`)}
                  >
                    <td className="px-4 py-3 font-semibold text-blue-600 whitespace-nowrap">
                      #{res.id}
                    </td>

                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs px-2 py-0.5 bg-gray-100 rounded border border-gray-200 font-semibold text-gray-800">
                          {res.plate}
                        </span>
                        {res.branchCode && (
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-100 uppercase">
                            {res.branchCode}
                          </span>
                        )}
                        <span className="text-xs text-gray-600">
                          {res.vehicleDesc !== "-" ? res.vehicleDesc : ""}
                        </span>
                      </div>
                    </td>

                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-xs font-medium text-gray-900">
                        {res.customerName}
                      </div>
                      {res.companyName && res.companyName !== "-" && (
                        <div className="text-[11px] text-gray-500">
                          {res.companyName}
                        </div>
                      )}
                    </td>

                    <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-600">
                      <div>Pick: {formatDisplayDate(res.pickup_date)}</div>
                      <div className="text-gray-400">
                        Ret: {formatDisplayDate(res.return_date)}
                      </div>
                    </td>

                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(
                          res.status
                        )}`}
                      >
                        {res.status || "Open"}
                      </span>
                    </td>

                    <td className="px-4 py-3 whitespace-nowrap text-xs font-bold text-gray-900">
                      {formatAmount(res.balance, res.currency)}
                    </td>

                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/reservations/${res.id}`);
                        }}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-md transition"
                      >
                        <span>Open</span>
                        <ChevronRightIcon className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ─── 4. PENDING COMPANIES ────────────────────────────────────────── */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <div className="p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-gray-200">
          <div>
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-orange-50 text-orange-600 rounded-lg">
                <BuildingOffice2Icon className="w-4 h-4" />
              </div>
              <h3 className="text-base font-bold text-gray-900">
                Pending Companies
              </h3>
              <span className="px-2 py-0.5 text-xs font-semibold bg-orange-50 text-orange-700 ring-1 ring-inset ring-orange-600/20 rounded-full">
                {pendingCompanies.length} Unbilled
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              Corporate client accounts with unbilled events requiring invoice sequencing.
            </p>
          </div>

          <button
            type="button"
            onClick={() => navigate("/companies")}
            className="inline-flex items-center text-xs font-semibold text-blue-600 hover:text-blue-800 transition"
          >
            View All Companies →
          </button>
        </div>

        <div className="dashboard-table-container">
          {pendingCompanies.length === 0 ? (
            <div className="p-8 text-center">
              <CheckCircleIcon className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
              <p className="text-sm font-medium text-gray-900">
                All companies are up to date!
              </p>
              <p className="text-xs text-gray-500">
                No corporate accounts have pending unbilled events.
              </p>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Company Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact Email
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Phone
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Billing Status
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {pendingCompanies.map((comp) => (
                  <tr
                    key={comp.id}
                    className="hover:bg-orange-50/40 transition cursor-pointer"
                    onClick={() => navigate(`/companies/${comp.id}`)}
                  >
                    <td className="px-4 py-3 font-semibold text-gray-900 whitespace-nowrap">
                      {comp.name}
                    </td>

                    <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-600">
                      {comp.email !== "-" ? (
                        <a
                          href={`mailto:${comp.email}`}
                          onClick={(e) => e.stopPropagation()}
                          className="text-blue-600 hover:underline"
                        >
                          {comp.email}
                        </a>
                      ) : (
                        "-"
                      )}
                    </td>

                    <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-600">
                      {comp.phone}
                    </td>

                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium text-orange-700 ring-1 ring-inset ring-orange-600/20 bg-orange-50">
                        Pending
                      </span>
                    </td>

                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/companies/${comp.id}`);
                        }}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-orange-700 bg-orange-50 hover:bg-orange-100 rounded-md transition"
                      >
                        <span>Open</span>
                        <ChevronRightIcon className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
